import { useCallback } from 'react';

const AMOUNT_RULES = {
  valueMustBeGreaterThanCurrent: false,
  valueMustBeLessThanOrEqualToCurrent: false,
};

const FIELDS_ENABLED_FOR_EDIT = [
  {
    session: 'choice_figurant',
    step: 4,
    fields: ['arrivedTime', 'amount'],
    rules: {
      amount: {
        ...AMOUNT_RULES,
      },
    },
    nextStep: null,
  },
  {
    session: 'recording_info',
    step: 2,
    fields: [],
    nextStep: 4,
  },
];

const FIELDS_ENABLED_FOR_EXTENSION = [
  {
    session: 'choice_figurant',
    step: 4,
    fields: ['arrivedTime'],
    rules: {
      amount: {
        ...AMOUNT_RULES,
        valueMustBeLessThanOrEqualToCurrent: true,
      },
    },
  },
];

type TSessionField = {
  session: string;
  field: string;
};

type TIsValidateField = {
  session: string;
  field: string;
  value?: number;
  newValue: number;
  type: 'EDIT' | 'EXTENSION';
};

const findRulesBySession = (
  session: string,
  rulesForType: typeof FIELDS_ENABLED_FOR_EDIT | typeof FIELDS_ENABLED_FOR_EXTENSION
) => {
  for (const { session: itemSession, rules } of rulesForType) {
    if (itemSession === session) {
      return rules;
    }
  }
  return null;
};

export const useCanEditField = () => {
  const isValidateField = useCallback(({ session, field, value, newValue, type }: TIsValidateField) => {
    if (!value) {
      return false;
    }

    const rulesForType = {
      EDIT: FIELDS_ENABLED_FOR_EDIT,
      EXTENSION: FIELDS_ENABLED_FOR_EXTENSION,
    }[type];

    const rules = findRulesBySession(session, rulesForType);

    if (rules) {
      const rule = rules[field as keyof typeof rules] ?? null;
      if (rule) {
        if (rule.valueMustBeGreaterThanCurrent) {
          if (value > newValue) {
            return false;
          }
        }

        if (rule.valueMustBeLessThanOrEqualToCurrent) {
          if (newValue > value) {
            return false;
          }
        }
      }
    }

    return true;
  }, []);

  const canEditFieldBySession = useCallback((session: string) => {
    const sessions = FIELDS_ENABLED_FOR_EDIT.map(item => item.session);
    return sessions?.includes(session);
  }, []);

  const canEditField = useCallback(({ session, field }: TSessionField) => {
    const fields = FIELDS_ENABLED_FOR_EDIT.find(item => item.session === session)?.fields ?? [];
    return fields?.includes(field);
  }, []);

  const getStepsForEdit = () => {
    const steps = FIELDS_ENABLED_FOR_EDIT.map(item => item.step);
    return [...new Set(steps)];
  };

  const getStep = (id: number) => {
    const step = FIELDS_ENABLED_FOR_EDIT.find(item => item.step === id);
    return step;
  };

  return { canEditField, canEditFieldBySession, isValidateField, getStepsForEdit, getStep };
};

export default useCanEditField;
